import {
  AuthContext,
  capitalize,
  useApi,
  Payment,
  CommonContext,
} from '@repo-level/core-front';
import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute, useNavigation } from 'react-navi';

import { AnalyticsEventName } from 'api/analytics';
import { Button, ButtonCross } from '../buttons';
import { PaymentMethod as PaymentMethodComp } from './PaymentMethod';
import { fetchPaymentMethods } from 'helpers/payments';
import { ModalDefaultProps } from 'components/Modal';
import { UserWallets } from 'components/general/Header/UserWallets';
import { InputPromo } from './InputPromo';
import { DepositBonuses } from './DepositBonuses';
import { Spinner } from 'components/Spinner';
import { groupMemberBR } from 'helpers/user';
import { FullInformation } from 'components/auth/FullInformation';
import { PaymentMethodCard } from './PaymentMethod/components/PaymentMethodCard';
import { Registration } from 'components/auth/Registration';
import { checkCompleteUserInformation } from './helpers';
import { ModalsContext, ModalType } from 'contexts/modal';
import {
  LocalStorageDeposit,
  removeQueryParameterWithNavigation,
} from 'helpers/common';
import { useAnalytics } from 'helpers/analytics';
import { Country } from 'helpers/conf';

import {
  ScCurrencyUser,
  ScDepositContent,
  ScDepositModal,
  ScDepositNav,
  ScDepositRow,
  ScDepositTitle,
  ScNotFound,
  ScPaymentsMethods,
} from './styled';

export const Deposit = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose, onScrollTo }, ref) => {
    const { user, pendingUser } = useContext(AuthContext);
    const { currentIp, currencies } = useContext(CommonContext);

    const navigation = useNavigation();
    const {
      url: { query },
    } = useCurrentRoute();

    const removeVipQuery = useCallback(() => {
      if (query.vip) {
        removeQueryParameterWithNavigation(navigation, query, 'vip');
      }
    }, [navigation, query]);

    const handleClose = useCallback(() => {
      removeVipQuery();

      onClose && onClose();
    }, [onClose, removeVipQuery]);

    const handleCloseDeposit = useCallback(() => {
      removeVipQuery();

      LocalStorageDeposit.remove();

      onClose && onClose();
    }, [onClose, removeVipQuery]);

    if (user?.id == null && !pendingUser) {
      return <Registration onClose={handleClose} ref={ref} />;
    }

    if (
      (!checkCompleteUserInformation(
        user,
        currentIp?.countryCode === Country.France
      ) ||
        user?.state === null) &&
      currencies.find(({ code }) => code === user?.currency)?.fiat
    ) {
      return <FullInformation onClose={handleClose} ref={ref} />;
    }

    return (
      <ScDepositModal ref={ref}>
        <ButtonCross top={8} right={8} onClick={handleCloseDeposit} />
        <DepositComponent onScrollTo={onScrollTo} />
      </ScDepositModal>
    );
  }
);

const DepositComponent: FC<ModalDefaultProps> = ({ onScrollTo }) => {
  const { t } = useTranslation();

  const refDepositForm = useRef<HTMLDivElement>(null);

  const { user, wallets } = useContext(AuthContext);

  const { sendEventToAnalytics } = useAnalytics();

  const isBonusRestricted = useMemo(() => groupMemberBR(user), [user]);

  const [
    currentPaymentMethod,
    setCurrentPaymentMethod,
  ] = useState<Payment.Method>();

  const [
    requestPaymentMethods,
    { data: paymentMethods, pending: pendingPaymentMethods },
  ] = useApi(fetchPaymentMethods, undefined);

  const localPaymentMethod = LocalStorageDeposit.get().paymentMethod;

  const isLocalMethodAvailable = useMemo(() => {
    return (
      paymentMethods?.some((method) => method.id === localPaymentMethod?.id) ??
      false
    );
  }, [paymentMethods]);

  useEffect(() => {
    if (user?.currency == null) return;
    requestPaymentMethods(user?.currency);
  }, [requestPaymentMethods, user]);

  useEffect(() => {
    if (pendingPaymentMethods || paymentMethods == null) return;

    if (!!localPaymentMethod && isLocalMethodAvailable) {
      setCurrentPaymentMethod(localPaymentMethod);
      return;
    }

    setCurrentPaymentMethod(paymentMethods[0]);

    return () => {
      setCurrentPaymentMethod(undefined);
    };
  }, [
    setCurrentPaymentMethod,
    paymentMethods,
    pendingPaymentMethods,
    isLocalMethodAvailable,
  ]);

  const handleSelectPaymentMethods = useCallback(
    (method: Payment.Method) => {
      setCurrentPaymentMethod(method);
      LocalStorageDeposit.push({ paymentMethod: method });

      onScrollTo && onScrollTo(refDepositForm);
    },
    [onScrollTo]
  );

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__FORM__APPEARANCE,
      attributes: {
        additionalData: JSON.stringify({ from: window.location.href }),
      },
    });
  }, [sendEventToAnalytics]);

  if (user?.currency == null) return null;

  return (
    <>
      <ScDepositTitle>{t('glossary:deposit')}</ScDepositTitle>
      <ScDepositRow>
        <ScDepositNav>
          <CurrencyUser />
          <PaymentsMethods
            title={t('deposits:allPaymentMethod')}
            currentMethod={currentPaymentMethod}
            methods={paymentMethods}
            pending={pendingPaymentMethods}
            onSelect={handleSelectPaymentMethods}
          />
        </ScDepositNav>
        <ScDepositContent ref={refDepositForm}>
          {pendingPaymentMethods ? (
            <Spinner size={64} center />
          ) : currentPaymentMethod == null ? (
            <ScNotFound>
              {capitalize(t('deposits:notSelectedMethod'))}
            </ScNotFound>
          ) : (
            <PaymentMethodComp
              userCurrency={user.currency}
              paymentMethod={currentPaymentMethod}
              paymentAction={Payment.Action.Deposit}
              setCurrentPaymentMethod={setCurrentPaymentMethod}
            />
          )}
          {!isBonusRestricted && (
            <>
              <InputPromo pending={pendingPaymentMethods} />
              {user?.canIssueBonuses && <DepositBonuses />}
            </>
          )}
        </ScDepositContent>
      </ScDepositRow>
    </>
  );
};

const CurrencyUser = () => {
  const { t } = useTranslation();

  const { handleModal } = useContext(ModalsContext);

  return (
    <ScCurrencyUser>
      <h3>{t('glossary:currency')}</h3>
      <div className="row">
        <div className="selector">
          <UserWallets
            hideDepositButton
            darkSelector
            disableSelectionForOneCurrency
          />
        </div>
        <Button
          type="button"
          whiteTransparent
          medium
          wide
          onClick={handleModal(ModalType.EditCurrencies, {
            stayInSameWindow: true,
          })}
        >
          {t('glossary:addCurrency')}
        </Button>
      </div>
    </ScCurrencyUser>
  );
};

interface PaymentsMethodsProps {
  title: string;
  pending: boolean;
  onSelect: (method: Payment.Method) => void;
  currentMethod?: Payment.Method;
  methods?: Payment.Method[];
}

const PaymentsMethods: FC<PaymentsMethodsProps> = ({
  title,
  currentMethod,
  methods,
  pending,
  onSelect,
}) => {
  const { t } = useTranslation('deposits');

  return (
    <ScPaymentsMethods>
      <h3>{capitalize(title)}</h3>
      {pending ? (
        <Spinner size={64} center />
      ) : methods == null || methods.length === 0 ? (
        <ScNotFound>{t('notFoundPaymentsMethods')}</ScNotFound>
      ) : (
        <div className="payments">
          {methods.map((method, index) => (
            <PaymentMethodCard
              key={method.id}
              method={method}
              index={index}
              onSelect={onSelect}
              currentMethod={currentMethod}
            />
          ))}
        </div>
      )}
    </ScPaymentsMethods>
  );
};
